import { Switch, Route, useLocation, useRoute, useSearch, Redirect, Link } from "wouter";
import { useEffect, useLayoutEffect, useRef, useState, useCallback, lazy, Suspense } from "react";
import * as Sentry from "@sentry/react";
import { CookieConsentBanner } from "@/components/cookie-consent-banner";
import { CookiePreferencesModal } from "@/components/cookie-preferences-modal";
import { shouldShowCookieConsentBanner } from "@/lib/cookie-banner-visibility";
import {
  getConsent,
  isConsentStale,
  setConsent,
  recordConsentEvent,
  subscribe as subscribeConsent,
  ALL_GRANTS,
  ESSENTIAL_ONLY_GRANTS,
  type ConsentGrants,
  type ConsentRecord,
} from "@/lib/consent-store";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { analytics } from "@/lib/analytics";
import { isPublicPageViewPath, redactedAnalyticsLocation } from "@shared/site-pages-inventory";
import { withSearch } from "@shared/growth-story";
import { contactIdFromSearch } from "@/lib/visitor-link";
import { Header } from "@/components/header";
import { CompassIntro } from "@/components/compass-intro";
import { CompassLoader } from "@/components/compass-loader";
import { ImpersonationBanner } from "@/components/impersonation-banner";
import { PortalTransitionOverlay } from "@/components/portal-transition-overlay";
import { PreviewBanner } from "@/components/preview-banner";
import { ForcePasswordChange } from "@/components/force-password-change";
import { TermsAcceptanceGate } from "@/components/terms-acceptance-gate";
import { PasskeySetupPrompt } from "@/components/passkey-setup-prompt";
import { InvestorWelcomeModal } from "@/components/investor-welcome-modal";
import { investorWelcomeShellState, useInvestorWelcome } from "@/lib/investor-welcome-state";
import { ErrorBoundary } from "@/components/error-boundary";
import { isStaleChunkError, attemptStaleChunkReload } from "@/lib/stale-chunk";
import { SecuritiesDisclaimer } from "@/components/securities-disclaimer";
import { SITE_FOOTER_DISCLAIMER } from "@shared/aeo-copy";
import { WhatsAppFloatingButton, type WhatsAppAudience } from "@/components/whatsapp-button";
// Auth context providers and hooks are kept eager because AppContent reads
// them synchronously to decide which portal shell to render. Everything else
// below is a route page, lazily loaded so each route's code is fetched only
// when that route is first visited (Task #2673 — bundle splitting).
import { VendorAuthProvider, useVendorAuth } from "@/hooks/use-vendor-auth";
import { AgentAuthProvider, useAgentAuth } from "@/hooks/use-agent-auth";
import { PrincipalAuthProvider, usePrincipalAuth } from "@/hooks/use-principal-auth";

// Home stays eager so the public landing page paints without a second
// network round-trip for its chunk.
import Home from "@/pages/home";

const NotFound = lazy(() => import("@/pages/not-found"));
const UnsubscribePage = lazy(() => import("@/pages/unsubscribe"));
const VisaBulletin = lazy(() => import("@/pages/visa-bulletin"));
const Countries = lazy(() => import("@/pages/countries"));
const CountryDetail = lazy(() => import("@/pages/country-detail"));
const KnowledgeBase = lazy(() => import("@/pages/knowledge-base"));
const Legislation = lazy(() => import("@/pages/legislation"));
const Forum = lazy(() => import("@/pages/forum"));
const RegionalCenters = lazy(() => import("@/pages/regional-centers"));
const Login = lazy(() => import("@/pages/login"));
const LogoutPage = lazy(() => import("@/pages/logout"));
const Register = lazy(() => import("@/pages/register"));
const ForgotPassword = lazy(() => import("@/pages/forgot-password"));
const ResetPassword = lazy(() => import("@/pages/reset-password"));
const EmailChangeActionPage = lazy(() => import("@/pages/email-change-action"));
const AccountSettings = lazy(() => import("@/pages/investor/account-settings"));
const DirectEB5 = lazy(() => import("@/pages/direct-eb5"));
const GoldCard = lazy(() => import("@/pages/gold-card"));
const News = lazy(() => import("@/pages/news"));
const MediaPage = lazy(() => import("@/pages/media"));
const ArticlePage = lazy(() => import("@/pages/article"));
const SourceOfFunds = lazy(() => import("@/pages/source-of-funds"));
const Eb5MinimumInvestmentPage = lazy(() => import("@/pages/eb5-minimum-investment"));
const EB5Guide = lazy(() => import("@/pages/eb5-guide"));
// Unlisted screen-share sales walkthrough — reachable only by direct URL,
// deliberately not added to the site navigation and marked noindex.
const Overview = lazy(() => import("@/pages/overview"));
const EnergyBriefing = lazy(() => import("@/pages/energy-briefing"));
const PartnerBriefing = lazy(() => import("@/pages/partner-briefing"));
const AgentInvestorPresentation = lazy(() => import("@/pages/agent/investor-presentation"));
const InvestorPresentationPage = lazy(() => import("@/pages/investor/presentation"));
const PricingPage = lazy(() => import("@/pages/pricing"));
const FundsAssessment = lazy(() => import("@/pages/funds-assessment"));
const RcReviews = lazy(() => import("@/pages/rc-reviews"));
const InvestorVerification = lazy(() => import("@/pages/investor-verification"));
const Projects = lazy(() => import("@/pages/projects"));
const ProjectDetailPage = lazy(() => import("@/pages/project-detail"));
const ComparePage = lazy(() => import("@/pages/compare"));
const InvestorProjectDetail = lazy(() => import("@/pages/investor/project-detail"));
const AdminDashboard = lazy(() => import("@/pages/admin/dashboard"));
const AdminDeskActivity = lazy(() => import("@/pages/admin/desk-activity"));
const AdminPipeline = lazy(() => import("@/pages/admin/pipeline"));
const AdminPipelineDetail = lazy(() => import("@/pages/admin/pipeline-detail"));
const AdminPipelineMap = lazy(() => import("@/pages/admin/pipeline-map"));
const AdminFinance = lazy(() => import("@/pages/admin/finance"));
const AdminPartnerPricing = lazy(() => import("@/pages/admin/finance-partner-pricing"));
const AdminFinanceModel = lazy(() => import("@/pages/admin/finance-model"));
const AdminOutstandingPayments = lazy(() => import("@/pages/admin/outstanding-payments"));
const AdminFinances = lazy(() => import("@/pages/admin/finances"));
const AdminPartnerPrograms = lazy(() => import("@/pages/admin/partner-programs"));
const AdminPortfolioForecast = lazy(() => import("@/pages/admin/portfolio-forecast"));
const AdminProjectSettings = lazy(() => import("@/pages/admin/project-settings"));
const AdminProjectSettingsDivision = lazy(() => import("@/pages/admin/project-settings-division"));
const AdminAiChatbot = lazy(() => import("@/pages/admin/ai-chatbot"));
const AdminInvestors = lazy(() => import("@/pages/admin/investors"));
const AdminConsultations = lazy(() => import("@/pages/admin/consultations"));
const AdminPages = lazy(() => import("@/pages/admin/pages"));
const AdminPresentations = lazy(() => import("@/pages/admin/presentations"));
const AdminMarketingDashboard = lazy(() => import("@/pages/admin/marketing-dashboard"));
const AdminContent = lazy(() => import("@/pages/admin/content"));
const AdminContentFeedback = lazy(() => import("@/pages/admin/content-feedback"));
const AdminContentIdeas = lazy(() => import("@/pages/admin/content-ideas"));
const AdminContentAttribution = lazy(() => import("@/pages/admin/content-attribution"));
const AdminConsentRegister = lazy(() => import("@/pages/admin/consent-register"));
const AdminQrCodes = lazy(() => import("@/pages/admin/qr-codes"));
const AdminApiManagement = lazy(() => import("@/pages/admin/api-management"));
const AdminInbox = lazy(() => import("@/pages/admin/inbox"));
const AdminLeadOwnership = lazy(() => import("@/pages/admin/lead-ownership"));
const AdminContacts = lazy(() => import("@/pages/admin/contacts"));
const AdminOrganizations = lazy(() => import("@/pages/admin/organizations"));
const AdminOrganizationDetail = lazy(() => import("@/pages/admin/organization-detail"));
const AdminHotBoard = lazy(() => import("@/pages/admin/hot-board"));
const AdminWhoWeCouldSellTo = lazy(() => import("@/pages/admin/who-we-could-sell-to"));
const AdminRoutingViews = lazy(() => import("@/pages/admin/routing-views"));
const AdminEmailSequences = lazy(() => import("@/pages/admin/email-sequences"));
const AdminConversations = lazy(() => import("@/pages/admin/conversations"));
const AdminWeeklyPullList = lazy(() => import("@/pages/admin/weekly-pull-list"));
const AdminThisWeek = lazy(() => import("@/pages/admin/this-week"));
const AdminCanPeopleFindUs = lazy(() => import("@/pages/admin/can-people-find-us"));
const AdminEmailDeliverability = lazy(() => import("@/pages/admin/email-deliverability"));
const AdminDeleted = lazy(() => import("@/pages/admin/deleted"));
const AdminAttorneys = lazy(() => import("@/pages/admin/attorneys"));
const AdminEconomists = lazy(() => import("@/pages/admin/economists"));
const AdminBusinessPlanWriters = lazy(() => import("@/pages/admin/business-plan-writers"));
const AdminFundAdministrators = lazy(() => import("@/pages/admin/fund-administrators"));
const AdminVendors = lazy(() => import("@/pages/admin/vendors"));
const AdminAgents = lazy(() => import("@/pages/admin/agents"));
const AgentLanding = lazy(() => import("@/pages/agent-landing"));
const AgentApply = lazy(() => import("@/pages/agent-apply"));
const AgentsLeadReferral = lazy(() => import("@/pages/agents-lead-referral"));
const AgentApplicationStatus = lazy(() => import("@/pages/agent-application-status"));
const DocumentRequestPage = lazy(() => import("@/pages/document-request"));
const AdminCalendarSettings = lazy(() => import("@/pages/admin/calendar-settings"));
const AdminCompliance = lazy(() => import("@/pages/admin/compliance"));
const AdminComplianceExport = lazy(() => import("@/pages/admin/compliance-export"));
const AdminTeam = lazy(() => import("@/pages/admin/team"));
const AdminProfiles = lazy(() => import("@/pages/admin/profiles"));
const AdminEntities = lazy(() => import("@/pages/admin/entities"));
const AdminEntityDetail = lazy(() => import("@/pages/admin/entity-detail"));
const AdminSystemActivity = lazy(() => import("@/pages/admin/system-activity"));
const AdminSystemHealth = lazy(() => import("@/pages/admin/system-health"));
const AdminQuality = lazy(() => import("@/pages/admin/quality"));
const AdminEsign = lazy(() => import("@/pages/admin/esign"));
const EsignSign = lazy(() => import("@/pages/esign/sign"));
const AdminSecurity = lazy(() => import("@/pages/admin/security"));
const AdminMySchedule = lazy(() => import("@/pages/admin/my-schedule"));
const InvestorMySchedule = lazy(() => import("@/pages/investor/my-schedule"));
const AdminProfile = lazy(() => import("@/pages/admin/profile"));
const AdminBrandIntelligence = lazy(() => import("@/pages/admin/brand-intelligence"));
const AdminDemoScript = lazy(() => import("@/pages/admin/demo-script"));
const AdminPartnerPitch = lazy(() => import("@/pages/admin/partner-pitch"));
const AdminPartnerMaterials = lazy(() => import("@/pages/admin/partner-materials"));
const AdminPartnerPitchStage = lazy(() => import("@/pages/admin/partner-pitch-stage"));
const AdminPartnerPitchConsole = lazy(() => import("@/pages/admin/partner-pitch-console"));
const ScheduleCall = lazy(() => import("@/pages/schedule-call"));
const DealRoom = lazy(() => import("@/pages/deal-room"));
const MarketingLanding = lazy(() => import("@/pages/marketing-landing"));
const MarketingSharePage = lazy(() => import("@/pages/marketing-share"));
const PitchSharePage = lazy(() => import("@/pages/pitch-share"));
const EnergyDivisionPage = lazy(() => import("@/pages/divisions/energy"));
const RealEstateDivisionPage = lazy(() => import("@/pages/divisions/real-estate"));
const Sitemap = lazy(() => import("@/pages/sitemap"));
const AboutPage = lazy(() => import("@/pages/about"));
const InvestorDashboard = lazy(() => import("@/pages/investor/dashboard"));
const InvestorProjects = lazy(() => import("@/pages/investor/projects"));
const InvestorAccreditation = lazy(() => import("@/pages/investor/deal-room-entry"));
const RegSEligibility = lazy(() => import("@/pages/investor/reg-s-eligibility"));
const InvestorConsultations = lazy(() => import("@/pages/investor/consultations"));
const InvestorDealRooms = lazy(() => import("@/pages/investor/deal-rooms"));
const SEOContentPage = lazy(() => import("@/pages/seo-content-page"));
const VendorSetPasswordPage = lazy(() => import("@/pages/vendor/set-password"));
const VendorResetPasswordPage = lazy(() => import("@/pages/vendor/reset-password"));
const VendorDashboardPage = lazy(() => import("@/pages/vendor/dashboard"));
const VendorProjectDetailPage = lazy(() => import("@/pages/vendor/project-detail"));
const AgentSetPasswordPage = lazy(() => import("@/pages/agent/set-password"));
const AgentDocumentAccessPage = lazy(() => import("@/pages/agent/document-access"));
const AgentResetPasswordPage = lazy(() => import("@/pages/agent/reset-password"));
const AgentForgotPasswordPage = lazy(() => import("@/pages/agent/forgot-password"));
const AgentVerifyEmailPage = lazy(() => import("@/pages/agent/verify-email"));
const AgentDashboardPage = lazy(() => import("@/pages/agent/dashboard"));
const AgentInvestorsPage = lazy(() => import("@/pages/agent/investors"));
const AgentProjectsPage = lazy(() => import("@/pages/agent/projects"));
const AgentProjectDetailPage = lazy(() => import("@/pages/agent/project-detail"));
const AgentDealRoomPage = lazy(() => import("@/pages/agent/deal-room"));
const AgentProfilePage = lazy(() => import("@/pages/agent/profile"));
const AgentDocumentsPage = lazy(() => import("@/pages/agent/documents"));
const AgentEsignPage = lazy(() => import("@/pages/agent/esign"));
const AgentInboxPage = lazy(() => import("@/pages/agent/inbox"));
const InvestorInboxPage = lazy(() => import("@/pages/investor/inbox"));
const InvestorReferralsPage = lazy(() => import("@/pages/investor/referrals"));
const AgentMarketingPage = lazy(() => import("@/pages/agent/marketing"));
const AgentMaterialsPage = lazy(() => import("@/pages/agent/materials"));
const MaterialsPresentPage = lazy(() => import("@/pages/materials-present"));
const PresentationsSharePage = lazy(() => import("@/pages/presentations-share"));
const AgentEarningsPage = lazy(() => import("@/pages/agent/earnings"));
const AgentWhiteLabelPage = lazy(() => import("@/pages/agent/white-label"));
const InboxPage = lazy(() => import("@/pages/inbox"));
const NotificationsPreferencesPage = lazy(() => import("@/pages/notifications-preferences"));
const MessagesPage = lazy(() => import("@/pages/messages"));
const AdminComplianceMessages = lazy(() => import("@/pages/admin/compliance-messages"));
const AdminCompliancePackDownloads = lazy(() => import("@/pages/admin/compliance-pack-downloads"));
const AdminComplianceAuditPackage = lazy(() => import("@/pages/admin/compliance-audit-package"));
const SponsorApply = lazy(() => import("@/pages/sponsor-apply"));
const SponsorApplicationStatus = lazy(() => import("@/pages/sponsor-application-status"));
const SponsorSetPasswordPage = lazy(() => import("@/pages/sponsor/set-password"));
const SponsorDashboard = lazy(() => import("@/pages/sponsor/dashboard"));
const SponsorProjectDetail = lazy(() => import("@/pages/sponsor/project-detail"));
const SponsorNotifications = lazy(() => import("@/pages/sponsor/notifications"));
const PrincipalSetPasswordPage = lazy(() => import("@/pages/principals/set-password"));
const PrincipalDashboard = lazy(() => import("@/pages/principals/dashboard"));
const PrincipalProjectPage = lazy(() => import("@/pages/principals/project"));
const TermsOfService = lazy(() => import("@/pages/terms"));
const PrivacyPolicy = lazy(() => import("@/pages/privacy"));
const TimelineCalculator = lazy(() => import("@/pages/timeline-calculator"));

function CookieConsentManager() {
  const [location] = useLocation();
  const { user } = useAuth();
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<ConsentRecord | null>(null);
  const linkedUserRef = useRef<string | null>(null);

  // Login-time consent linkage: when a visitor who already has a valid local
  // consent record authenticates, emit a `changed` audit event so the server
  // can associate prior anonymous consent with their userId. We guard with
  // sessionStorage so this fires at most once per browser session per user.
  useEffect(() => {
    if (!user?.id) return;
    if (linkedUserRef.current === user.id) return;
    const sessionKey = `consent_linked_${user.id}`;
    if (typeof window !== "undefined" && sessionStorage.getItem(sessionKey)) {
      linkedUserRef.current = user.id;
      return;
    }
    const stored = getConsent();
    if (!stored || isConsentStale(stored)) return;
    linkedUserRef.current = user.id;
    try { sessionStorage.setItem(sessionKey, "1"); } catch {}
    recordConsentEvent({ ...stored, choiceKind: "changed" }, window.location.href);
  }, [user?.id]);

  useEffect(() => {
    const stored = getConsent();
    setCurrentRecord(stored);
    setShowBanner(isConsentStale(stored));

    const unsub = subscribeConsent((record) => {
      setCurrentRecord(record);
      setShowBanner(false);
    });

    const handleOpenModal = () => setShowModal(true);
    window.addEventListener("open-cookie-preferences", handleOpenModal);

    return () => {
      unsub();
      window.removeEventListener("open-cookie-preferences", handleOpenModal);
    };
  }, []);

  // Reserve bottom space while the banner is visible so fixed CTAs / form
  // submit buttons on /refer and /agents/apply are not covered (clicks on
  // the banner strip were swallowing Create Account — looked like a no-op).
  const bannerVisible = shouldShowCookieConsentBanner({
    showBanner,
    showModal,
    isAuthenticated: !!user,
    location,
  });

  useEffect(() => {
    const root = document.documentElement;
    if (bannerVisible) {
      root.setAttribute("data-cookie-banner", "open");
      // /refer uses a one-line bar so the partner name stays on screen.
      const offset = location.startsWith("/refer/") ? "6.5rem" : "18rem";
      root.style.setProperty("--cookie-banner-offset", offset);
    } else {
      root.removeAttribute("data-cookie-banner");
      root.style.removeProperty("--cookie-banner-offset");
    }
    return () => {
      root.removeAttribute("data-cookie-banner");
      root.style.removeProperty("--cookie-banner-offset");
    };
  }, [bannerVisible, location]);

  function clearBodyPointerEventsTrap() {
    // Radix modal Dialog sets body { pointer-events: none }. Leftover style
    // makes Accept / Essential Only look stuck even with pointer-events:auto
    // on the banner when a nested overlay is involved.
    try {
      if (document.body.style.pointerEvents === "none") {
        document.body.style.pointerEvents = "";
      }
    } catch {
      /* ignore */
    }
  }

  function handleAcceptAll() {
    clearBodyPointerEventsTrap();
    try {
      const prev = currentRecord;
      const choiceKind = prev && !isConsentStale(prev) ? "changed" : "accept_all";
      const record = setConsent(ALL_GRANTS, choiceKind, prev);
      recordConsentEvent(record, window.location.href);
    } finally {
      setShowBanner(false);
    }
  }

  function handleEssentialOnly() {
    clearBodyPointerEventsTrap();
    try {
      const prev = currentRecord;
      const choiceKind = prev && !isConsentStale(prev) ? "changed" : "essential_only";
      const record = setConsent(ESSENTIAL_ONLY_GRANTS, choiceKind, prev);
      recordConsentEvent(record, window.location.href);
    } finally {
      // Always dismiss so Essential Only cannot look stuck if storage fails.
      setShowBanner(false);
    }
  }

  function handleCustomize() {
    clearBodyPointerEventsTrap();
    setShowModal(true);
  }

  function handleSaveCustom(grants: ConsentGrants) {
    clearBodyPointerEventsTrap();
    try {
      const prev = currentRecord;
      const choiceKind = prev && !isConsentStale(prev) ? "changed" : "custom";
      const record = setConsent(grants, choiceKind, prev);
      recordConsentEvent(record, window.location.href);
    } finally {
      setShowBanner(false);
    }
  }

  return (
    <>
      {bannerVisible && (
        <CookieConsentBanner
          compact={location.startsWith("/refer/")}
          onAcceptAll={handleAcceptAll}
          onEssentialOnly={handleEssentialOnly}
          onCustomize={handleCustomize}
        />
      )}
      <CookiePreferencesModal
        open={showModal}
        onOpenChange={setShowModal}
        initialGrants={currentRecord?.grants}
        onSave={handleSaveCustom}
      />
    </>
  );
}

/**
 * Minimal footer rendered on every public page (except the home page which
 * already has a comprehensive footer). Gives visitors a persistent route to
 * update their cookie preferences from any page, satisfying GDPR's
 * "easily accessible" requirement.
 */
function SiteFooter() {
  return (
    <footer
      className="relative z-[45] bg-[#061828] text-white/50 py-5 text-xs border-t border-white/10"
      data-testid="site-footer"
    >
      <div className="container mx-auto px-4 flex flex-col gap-3">
        <p className="text-white/70 text-xs leading-relaxed" data-testid="text-site-footer-disclaimer">
          {SITE_FOOTER_DISCLAIMER}
        </p>
        <SecuritiesDisclaimer className="text-white/40 text-[11px] leading-relaxed" />
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 border-t border-white/10 pt-3">
        <p>
          &copy; {new Date().getFullYear()} Veras Capital. Not affiliated with USCIS or any government agency.
        </p>
        <p className="flex items-center gap-3">
          <Link href="/terms" className="hover:text-white/80 transition-colors" data-testid="link-site-footer-terms">
            Terms
          </Link>
          <span aria-hidden>·</span>
          <Link href="/privacy" className="hover:text-white/80 transition-colors" data-testid="link-site-footer-privacy">
            Privacy
          </Link>
          <span aria-hidden>·</span>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("open-cookie-preferences"))}
            className="hover:text-white/80 transition-colors cursor-pointer underline-offset-2 hover:underline"
            data-testid="link-site-footer-cookie-preferences"
          >
            Cookie preferences
          </button>
        </p>
        </div>
      </div>
    </footer>
  );
}

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

function PageTracker() {
  const [location] = useLocation();
  const [analyticsGranted, setAnalyticsGranted] = useState(() => {
    const current = getConsent();
    return current?.grants.analytics === true && !isConsentStale(current);
  });

  useEffect(() => {
    return subscribeConsent((record) => {
      setAnalyticsGranted(record.grants.analytics === true && !isConsentStale(record));
    });
  }, []);

  useEffect(() => {
    const path = window.location.pathname;
    const tracked = redactedAnalyticsLocation(path, window.location.href);
    const redacted = tracked.path !== path || tracked.url !== window.location.href;
    const pagePath = redacted ? tracked.path : location;
    const pageUrl = redacted ? tracked.url : window.location.href;
    analytics.page(pagePath, {
      path: pagePath,
      url: pageUrl,
      title: document.title,
    });
  }, [location]);

  useEffect(() => {
    const path = window.location.pathname;
    if (!isPublicPageViewPath(path)) return;
    const consent = getConsent();
    const analyticsGrant = analyticsGranted && consent?.grants.analytics === true && !isConsentStale(consent);
    const params = new URLSearchParams(window.location.search);
    let sessionId: string | undefined;
    if (analyticsGrant) {
      try {
        sessionId = sessionStorage.getItem("veras_page_session") ?? undefined;
        if (!sessionId) {
          sessionId = crypto.randomUUID();
          sessionStorage.setItem("veras_page_session", sessionId);
        }
      } catch {
        sessionId = undefined;
      }
    }
    fetch("/api/page-views", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        path,
        ...(analyticsGrant
          ? {
              analyticsGrant: true,
              visitorUuid: consent?.visitorUuid,
              sessionId,
              referrer: document.referrer || undefined,
              utmSource: params.get("utm_source") ?? undefined,
              utmMedium: params.get("utm_medium") ?? undefined,
              utmCampaign: params.get("utm_campaign") ?? undefined,
              utmTerm: params.get("utm_term") ?? undefined,
              utmContent: params.get("utm_content") ?? undefined,
              contactId: contactIdFromSearch(window.location.search),
            }
          : {}),
      }),
      credentials: "include",
      keepalive: true,
    }).catch(() => {
      /* never block navigation */
    });
  }, [location, analyticsGranted]);
  return null;
}

// Tracks whether the first lazy route chunk has ever resolved. The very first
// route load reuses the FULLSCREEN compass so the boot/auth loader stays up
// (covering the shell) until the destination is actually ready to paint —
// instead of briefly revealing a half-built page with a separate inline
// compass. Once that first route paints, page-to-page navigations fall back to
// the inline loader as before. Module-scoped so it resets on a hard reload but
// persists across client-side navigation. Task #2837.
let firstRouteResolved = false;

export function RouteFallback() {
  // Compass-styled route loader so page-to-page (lazy chunk) transitions feel
  // consistent with the boot/login compass loader instead of a bare spinner.
  // When this fallback unmounts, the suspended route has resolved, so mark the
  // first paint done.
  useEffect(() => {
    return () => {
      firstRouteResolved = true;
    };
  }, []);
  return (
    <div data-testid="route-loading">
      <CompassLoader variant={firstRouteResolved ? "inline" : "fullscreen"} />
    </div>
  );
}

function MarketingAnalyticsRedirect() {
  let range = "week";
  try {
    const period = new URLSearchParams(window.location.search).get("period");
    if (period === "7") range = "7";
    else if (period === "30") range = "30";
    else if (period === "90") range = "90";
    else if (period === "365") range = "365";
    else if (period === "9999") range = "all";
  } catch {
    /* ignore */
  }
  return <Redirect to={`/admin/marketing-dashboard?range=${range}`} />;
}

function KeepSearchRedirect({ to }: { to: string }) {
  return <Redirect to={withSearch(to, useSearch())} />;
}

function LegacyProjectsRedirect({
  id,
  section,
}: {
  id?: string;
  section?: string;
}) {
  const suffix =
    typeof window === "undefined"
      ? ""
      : `${window.location.search}${window.location.hash}`;
  const destination = id
    ? `/admin/projects/${id}/${section || "overview"}${suffix}`
    : `/admin/projects${suffix}`;
  return <Redirect to={destination} />;
}

/**
 * Logged-in investors should complete eligibility at `/dashboard/accreditation`
 * rather than the public `/investor-verification` marketing product.
 * Anonymous visitors still get the SEO page (and `/verify` still aliases it).
 */
function InvestorVerificationOrAccreditation() {
  const { user } = useAuth();
  if (user?.role === "user") {
    return <Redirect to="/dashboard/accreditation" />;
  }
  return <InvestorVerification />;
}

function VerifyAlias() {
  const { user } = useAuth();
  if (user?.role === "user") {
    return <Redirect to="/dashboard/accreditation" />;
  }
  return <Redirect to="/investor-verification" />;
}

function Router() {
  return (
    <>
    <ScrollToTop />
    <PageTracker />
    <Suspense fallback={<RouteFallback />}>
    <Switch>
      <Route path="/marketing-share/:token" component={MarketingSharePage} />
      <Route path="/pitch-share/:token" component={PitchSharePage} />
      <Route path="/materials/present/:token" component={MaterialsPresentPage} />
      <Route path="/presentations/share/:token" component={PresentationsSharePage} />
      <Route path="/" component={Home} />
      <Route path="/visa-bulletin" component={VisaBulletin} />
      <Route path="/countries" component={Countries} />
      <Route path="/countries/:code" component={CountryDetail} />
      <Route path="/knowledge-base" component={KnowledgeBase} />
      <Route path="/legislation" component={Legislation} />
      <Route path="/regional-centers" component={RegionalCenters} />
      <Route path="/forum" component={Forum} />
      <Route path="/news" component={News} />
      <Route path="/media" component={MediaPage} />
      <Route path="/watch"><Redirect to="/media" /></Route>
      <Route path="/news/eb-5-reauthorization">{() => <SEOContentPage slug="/news/eb-5-reauthorization" />}</Route>
      <Route path="/news/gold-card-visa-lawsuit-legal-challenges">{() => <SEOContentPage slug="/news/gold-card-visa-lawsuit-legal-challenges" />}</Route>
      <Route path="/news/eb-5-visa-bulletin-march-2026">{() => <SEOContentPage slug="/news/eb-5-visa-bulletin-march-2026" />}</Route>
      <Route path="/news/uscis-eb-5-fee-reduction-2025">{() => <SEOContentPage slug="/news/uscis-eb-5-fee-reduction-2025" />}</Route>
      <Route path="/news/eb-5-ria-sunset-2027">{() => <SEOContentPage slug="/news/eb-5-ria-sunset-2027" />}</Route>
      <Route path="/news/:slug" component={ArticlePage} />
      <Route path="/login" component={Login} />
      <Route path="/logout" component={LogoutPage} />
      <Route path="/sign-in"><Redirect to="/login" /></Route>
      <Route path="/register" component={Register} />
      <Route path="/verify" component={VerifyAlias} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/change-email" component={EmailChangeActionPage} />
      <Route path="/cancel-email-change" component={EmailChangeActionPage} />
      <Route path="/direct-eb5" component={DirectEB5} />
      <Route path="/gold-card" component={GoldCard} />
      <Route path="/source-of-funds" component={SourceOfFunds} />
      <Route path="/eb-5-minimum-investment" component={Eb5MinimumInvestmentPage} />
      <Route path="/eb5-guide" component={EB5Guide} />
      <Route path="/overview" component={Overview} />
      <Route path="/energy-briefing" component={EnergyBriefing} />
      <Route path="/partner-briefing" component={PartnerBriefing} />
      <Route path="/pricing" component={PricingPage} />
      <Route path="/funds-assessment" component={FundsAssessment} />
      <Route path="/rc-reviews" component={RcReviews} />
      <Route path="/investor-verification" component={InvestorVerificationOrAccreditation} />
      <Route path="/projects" component={Projects} />
      <Route path="/projects/:slug" component={ProjectDetailPage} />
      <Route path="/compare" component={ComparePage} />
      <Route path="/schedule" component={ScheduleCall} />
      <Route path="/schedule-call" component={ScheduleCall} />
      <Route path="/deal-room/:projectId" component={DealRoom} />
      <Route path="/dashboard/presentation" component={InvestorPresentationPage} />
      <Route path="/dashboard" component={InvestorDashboard} />
      <Route path="/dashboard/projects/:slug" component={InvestorProjectDetail} />
      <Route path="/dashboard/projects" component={InvestorProjects} />
      <Route path="/dashboard/accreditation/reg-s" component={RegSEligibility} />
      <Route path="/dashboard/accreditation" component={InvestorAccreditation} />
      <Route path="/dashboard/consultations" component={InvestorConsultations} />
      <Route path="/dashboard/my-schedule" component={InvestorMySchedule} />
      <Route path="/dashboard/deal-rooms" component={InvestorDealRooms} />
      <Route path="/dashboard/account" component={AccountSettings} />
      <Route path="/contact"><Redirect to="/schedule-call#written-inquiry" /></Route>
      <Route path="/timeline-calculator" component={TimelineCalculator} />
      <Route path="/terms" component={TermsOfService} />
      <Route path="/privacy" component={PrivacyPolicy} />
      <Route path="/sitemap" component={Sitemap} />
      <Route path="/about" component={AboutPage} />
      <Route path="/refer/:code" component={AgentLanding} />
      <Route path="/agent-landing"><Redirect to="/agents/apply" /></Route>
      <Route path="/agent/apply"><Redirect to="/agents/apply" /></Route>
      <Route path="/agents/apply" component={AgentApply} />
      <Route path="/agents/lead-referral" component={AgentsLeadReferral} />
      <Route path="/agents/application/:token" component={AgentApplicationStatus} />
      <Route path="/documents/request/:token" component={DocumentRequestPage} />
      {/* american-energy slug: redirect to the project's public page */}
      <Route path="/invest/american-energy"><Redirect to="/projects/american-energy" /></Route>
      <Route path="/invest/:slug" component={MarketingLanding} />
      <Route path="/divisions/energy" component={EnergyDivisionPage} />
      <Route path="/divisions/real-estate" component={RealEstateDivisionPage} />
      <Route path="/admin" component={AdminDashboard} />
      <Route path="/admin/activity" component={AdminDeskActivity} />
      <Route path="/admin/projects" component={AdminPipeline} />
      <Route path="/admin/projects/map" component={AdminPipelineMap} />
      <Route path="/admin/projects/:id/:section?" component={AdminPipelineDetail} />
      <Route path="/admin/ai-chatbot" component={AdminAiChatbot} />
      <Route path="/admin/pipeline/map"><Redirect to="/admin/projects/map" /></Route>
      <Route path="/admin/pipeline/:id/:section?">
        {(params: { id: string; section?: string }) => (
          <LegacyProjectsRedirect id={params.id} section={params.section} />
        )}
      </Route>
      <Route path="/admin/pipeline"><LegacyProjectsRedirect /></Route>
      <Route path="/admin/finance" component={AdminFinance} />
      <Route path="/admin/finance/partner-pricing" component={AdminPartnerPricing} />
      <Route path="/admin/finance/model" component={AdminFinanceModel} />
      <Route path="/admin/outstanding-payments" component={AdminOutstandingPayments} />
      <Route path="/admin/finances" component={AdminFinances} />
      <Route path="/admin/partner-programs" component={AdminPartnerPrograms} />
      <Route path="/admin/portfolio-forecast" component={AdminPortfolioForecast} />
      <Route path="/admin/project-settings" component={AdminProjectSettings} />
      <Route path="/admin/project-settings/:divisionSlug" component={AdminProjectSettingsDivision} />
      {/* Task #1171 — old flat-tab routes redirect into the restructured Project Settings landing. */}
      <Route path="/admin/project-types"><Redirect to="/admin/project-settings" /></Route>
      <Route path="/admin/timeline-assumptions"><Redirect to="/admin/project-settings" /></Route>
      <Route path="/admin/underwriting-benchmarks"><Redirect to="/admin/project-settings" /></Route>
      <Route path="/admin/asset-class-backfill"><Redirect to="/admin/project-settings" /></Route>
      <Route path="/admin/investors" component={AdminInvestors} />
      {/* Task #2461 — old verifications URL forwards to the pending-review queue. */}
      <Route path="/admin/verifications"><Redirect to="/admin/investors?needsReview=1" /></Route>
      <Route path="/admin/consultations" component={AdminConsultations} />
      <Route path="/admin/deal-room"><Redirect to="/admin/projects" /></Route>
      <Route path="/admin/marketing-dashboard" component={AdminMarketingDashboard} />
      <Route path="/admin/pages" component={AdminPages} />
      <Route path="/admin/presentations" component={AdminPresentations} />
      <Route path="/admin/marketing"><Redirect to="/admin/pages" /></Route>
      <Route path="/admin/content/feedback" component={AdminContentFeedback} />
      <Route path="/admin/content/ideas" component={AdminContentIdeas} />
      <Route path="/admin/content/attribution" component={AdminContentAttribution} />
      <Route path="/admin/content/consent" component={AdminConsentRegister} />
      <Route path="/admin/content" component={AdminContent} />
      <Route path="/admin/marketing-analytics" component={MarketingAnalyticsRedirect} />
      <Route path="/admin/qr-codes" component={AdminQrCodes} />
      <Route path="/admin/api" component={AdminApiManagement} />
      <Route path="/admin/inquiries"><Redirect to="/admin/inbox" /></Route>
      <Route path="/admin/contacts" component={AdminContacts} />
      <Route path="/admin/organizations" component={AdminOrganizations} />
      <Route path="/admin/organizations/:id" component={AdminOrganizationDetail} />
      <Route path="/admin/hot-board" component={AdminHotBoard} />
      <Route path="/admin/who-we-could-sell-to" component={AdminWhoWeCouldSellTo} />
      <Route path="/admin/tam"><KeepSearchRedirect to="/admin/who-we-could-sell-to" /></Route>
      <Route path="/admin/routing-views" component={AdminRoutingViews} />
      <Route path="/admin/email-sequences/:id" component={AdminEmailSequences} />
      <Route path="/admin/email-sequences" component={AdminEmailSequences} />
      <Route path="/admin/conversations" component={AdminConversations} />
      <Route path="/admin/outreach-list"><KeepSearchRedirect to="/admin/conversations" /></Route>
      <Route path="/admin/who-to-call" component={AdminWeeklyPullList} />
      <Route path="/admin/weekly-pull-list"><KeepSearchRedirect to="/admin/who-to-call" /></Route>
      <Route path="/admin/this-week" component={AdminThisWeek} />
      <Route path="/admin/weekly-review"><KeepSearchRedirect to="/admin/this-week" /></Route>
      <Route path="/admin/weekly-scorecard"><KeepSearchRedirect to="/admin/this-week" /></Route>
      <Route path="/admin/can-people-find-us" component={AdminCanPeopleFindUs} />
      <Route path="/admin/index-coverage"><KeepSearchRedirect to="/admin/can-people-find-us" /></Route>
      <Route path="/admin/email-deliverability" component={AdminEmailDeliverability} />
      <Route path="/admin/leads/ownership" component={AdminLeadOwnership} />
      {/* Task #2114 — Lead Attribution and Lead Conflicts merged into the
          unified Lead Ownership screen; old URLs deep-link to the right tab. */}
      <Route path="/admin/leads/attribution"><Redirect to="/admin/leads/ownership" /></Route>
      <Route path="/admin/leads/review-requests"><Redirect to="/admin/leads/ownership?tab=conflicts" /></Route>
      {/* Task #2109 — Notify Me signups moved into the Contacts hub. */}
      <Route path="/admin/leads/notify-signups"><Redirect to="/admin/contacts?tag=Notify%20Me" /></Route>
      <Route path="/admin/attorneys" component={AdminAttorneys} />
      <Route path="/admin/economists" component={AdminEconomists} />
      <Route path="/admin/business-plan-writers" component={AdminBusinessPlanWriters} />
      <Route path="/admin/fund-administrators" component={AdminFundAdministrators} />
      <Route path="/admin/vendors" component={AdminVendors} />
      <Route path="/admin/agents" component={AdminAgents} />
      <Route path="/admin/partner-pitch/:sessionId/stage" component={AdminPartnerPitchStage} />
      <Route path="/admin/partner-pitch/:sessionId/console" component={AdminPartnerPitchConsole} />
      <Route path="/admin/partner-pitch" component={AdminPartnerPitch} />
      <Route path="/admin/partner-materials" component={AdminPartnerMaterials} />
      <Route path="/admin/compliance/export" component={AdminComplianceExport} />
      <Route path="/admin/compliance" component={AdminCompliance} />
      <Route path="/admin/team" component={AdminTeam} />
      <Route path="/admin/profiles" component={AdminProfiles} />
      <Route path="/admin/entities" component={AdminEntities} />
      <Route path="/admin/entities/:id" component={AdminEntityDetail} />
      <Route path="/admin/security" component={AdminSecurity} />
      <Route path="/admin/system-activity" component={AdminSystemActivity} />
      <Route path="/admin/system-health" component={AdminSystemHealth} />
      <Route path="/admin/quality" component={AdminQuality} />
      <Route path="/admin/esign" component={AdminEsign} />
      <Route path="/esign/sign/:token" component={EsignSign} />
      <Route path="/admin/my-schedule" component={AdminMySchedule} />
      <Route path="/admin/profile" component={AdminProfile} />
      <Route path="/admin/calendar-settings" component={AdminCalendarSettings} />
      <Route path="/admin/deleted" component={AdminDeleted} />
      <Route path="/admin/brand-intelligence" component={AdminBrandIntelligence} />
      <Route path="/admin/demo-script" component={AdminDemoScript} />
      <Route path="/admin/inbox" component={AdminInbox} />
      <Route path="/admin/inbox/:id" component={AdminInbox} />
      <Route path="/admin/notifications/inbox">{() => <InboxPage preferencesHref="/admin/notifications" />}</Route>
      <Route path="/admin/notifications">{() => <NotificationsPreferencesPage inboxHref="/admin/notifications/inbox" variant="admin" />}</Route>
      <Route path="/admin/messages" component={MessagesPage} />
      <Route path="/admin/compliance/messages" component={AdminComplianceMessages} />
      <Route path="/admin/compliance/pack-activity" component={AdminCompliancePackDownloads} />
      <Route path="/admin/compliance/audit-package" component={AdminComplianceAuditPackage} />
      <Route path="/dashboard/inbox" component={InvestorInboxPage} />
      <Route path="/dashboard/inbox/:id" component={InvestorInboxPage} />
      <Route path="/dashboard/referrals" component={InvestorReferralsPage} />
      <Route path="/dashboard/notifications/inbox">{() => <InboxPage preferencesHref="/dashboard/notifications" />}</Route>
      <Route path="/dashboard/notifications">{() => <NotificationsPreferencesPage inboxHref="/dashboard/notifications/inbox" />}</Route>
      <Route path="/dashboard/messages" component={MessagesPage} />

      <Route path="/vendor/login"><Redirect to="/login" /></Route>
      <Route path="/vendor/set-password" component={VendorSetPasswordPage} />
      <Route path="/vendor/reset-password" component={VendorResetPasswordPage} />
      <Route path="/vendor/dashboard" component={VendorDashboardPage} />
      <Route path="/vendor/projects/:id" component={VendorProjectDetailPage} />
      <Route path="/agent/login"><Redirect to="/login" /></Route>
      <Route path="/agent/set-password" component={AgentSetPasswordPage} />
      <Route path="/agent/document-access" component={AgentDocumentAccessPage} />
      <Route path="/agent/reset-password" component={AgentResetPasswordPage} />
      <Route path="/agent/forgot-password" component={AgentForgotPasswordPage} />
      <Route path="/agent/verify-email" component={AgentVerifyEmailPage} />
      <Route path="/agent/dashboard" component={AgentDashboardPage} />
      <Route path="/agent/investors" component={AgentInvestorsPage} />
      <Route path="/agent/projects/:id/deal-room" component={AgentDealRoomPage} />
      <Route path="/agent/projects/:id" component={AgentProjectDetailPage} />
      <Route path="/agent/projects" component={AgentProjectsPage} />
      <Route path="/agent/inquiries"><Redirect to="/agent/inbox" /></Route>
      <Route path="/agent/marketing" component={AgentMarketingPage} />
      <Route path="/agent/materials" component={AgentMaterialsPage} />
      <Route path="/agent/investor-presentation" component={AgentInvestorPresentation} />
      <Route path="/agent/earnings" component={AgentEarningsPage} />
      <Route path="/agent/white-label" component={AgentWhiteLabelPage} />
      {/* Task #2075 — Payouts merged into the Earnings screen as a tab.
          Keep the old URL working by redirecting into that tab. */}
      <Route path="/agent/payouts"><Redirect to="/agent/earnings?tab=payouts" /></Route>
      <Route path="/agent/profile" component={AgentProfilePage} />
      <Route path="/agent/documents" component={AgentDocumentsPage} />
      <Route path="/agent/esign" component={AgentEsignPage} />
      <Route path="/agent/inbox" component={AgentInboxPage} />
      <Route path="/agent/inbox/:id" component={AgentInboxPage} />
      <Route path="/agent/notifications/inbox">{() => <InboxPage preferencesHref="/agent/notifications" />}</Route>
      <Route path="/agent/notifications">{() => <NotificationsPreferencesPage inboxHref="/agent/notifications/inbox" />}</Route>
      <Route path="/agent/messages" component={MessagesPage} />

      <Route path="/sponsors/apply" component={SponsorApply} />
      <Route path="/sponsors/application/:token" component={SponsorApplicationStatus} />
      <Route path="/sponsor/login"><Redirect to="/login" /></Route>
      <Route path="/sponsor/set-password" component={SponsorSetPasswordPage} />
      <Route path="/sponsor/dashboard" component={SponsorDashboard} />
      <Route path="/sponsor/projects/:id" component={SponsorProjectDetail} />
      <Route path="/sponsor/notifications" component={SponsorNotifications} />

      <Route path="/principals/set-password" component={PrincipalSetPasswordPage} />
      <Route path="/principals/projects/:projectId" component={PrincipalProjectPage} />
      <Route path="/principals/dashboard" component={PrincipalDashboard} />

      <Route path="/eb-5-visa">{() => <SEOContentPage slug="/eb-5-visa" />}</Route>
      <Route path="/eb-5-visa/:sub">{(params: { sub: string }) => <SEOContentPage slug={`/eb-5-visa/${params.sub}`} />}</Route>
      <Route path="/eb-5-regional-center">{() => <SEOContentPage slug="/eb-5-regional-center" />}</Route>
      <Route path="/eb-5-regional-center/:sub">{(params: { sub: string }) => <SEOContentPage slug={`/eb-5-regional-center/${params.sub}`} />}</Route>
      <Route path="/gold-card-visa">{() => <SEOContentPage slug="/gold-card-visa" />}</Route>
      <Route path="/eb-5-investment-requirements">{() => <SEOContentPage slug="/eb-5-investment-requirements" />}</Route>
      <Route path="/eb-5-investment-requirements/2027-threshold-increase"><Redirect to="/eb-5-minimum-investment" /></Route>
      <Route path="/eb-5-investment-requirements/:sub">{(params: { sub: string }) => <SEOContentPage slug={`/eb-5-investment-requirements/${params.sub}`} />}</Route>
      <Route path="/eb-5-processing-time">{() => <SEOContentPage slug="/eb-5-processing-time" />}</Route>
      <Route path="/eb-5-processing-time/:sub">{(params: { sub: string }) => <SEOContentPage slug={`/eb-5-processing-time/${params.sub}`} />}</Route>
      <Route path="/forms/:sub">{(params: { sub: string }) => <SEOContentPage slug={`/forms/${params.sub}`} />}</Route>
      <Route path="/compare/eb-5-vs-gold-card">{() => <SEOContentPage slug="/compare/eb-5-vs-gold-card" />}</Route>
      <Route path="/compare/eb-5-vs-eb-3">{() => <SEOContentPage slug="/compare/eb-5-vs-eb-3" />}</Route>
      <Route path="/compare/regional-center-vs-direct">{() => <SEOContentPage slug="/compare/regional-center-vs-direct" />}</Route>
      <Route path="/compare/eb-5-vs-e2">{() => <SEOContentPage slug="/compare/eb-5-vs-e2" />}</Route>
      <Route path="/faq/:sub">{(params: { sub: string }) => <SEOContentPage slug={`/faq/${params.sub}`} />}</Route>
      <Route path="/glossary/:sub">{(params: { sub: string }) => <SEOContentPage slug={`/glossary/${params.sub}`} />}</Route>

      <Route path="/unsubscribe/:token" component={UnsubscribePage} />
      <Route component={NotFound} />
    </Switch>
    </Suspense>
    </>
  );
}

// Task #1421 — Gate components for agent and vendor portals.
// These must live inside their respective providers so they can read
// the portal-specific auth context. AppContent mounts the right
// provider wrapper, and each gate reads needsTermsAcceptance from the
// portal's /me payload via the typed context (no `any` casts needed).
function AgentTermsGate() {
  const { needsTermsAcceptance, isLoading } = useAgentAuth();
  // Task #2807 — wait for the portal's own /me check to resolve before
  // deciding. Without this the terms decision can land after the shell has
  // already painted, making the modal pop in a beat late.
  if (isLoading || !needsTermsAcceptance) return null;
  return <TermsAcceptanceGate queryKey="/api/agent/me" />;
}

function VendorTermsGate() {
  const { needsTermsAcceptance, isLoading } = useVendorAuth();
  // Task #2807 — wait for the portal's own /me check to resolve before
  // deciding (see AgentTermsGate above).
  if (isLoading || !needsTermsAcceptance) return null;
  return <TermsAcceptanceGate queryKey="/api/vendor/me" />;
}

function AgentPasskeyPrompt() {
  const { needsTermsAcceptance } = useAgentAuth();
  return <PasskeySetupPrompt blocked={needsTermsAcceptance} />;
}

function VendorPasskeyPrompt() {
  const { needsTermsAcceptance } = useVendorAuth();
  return <PasskeySetupPrompt blocked={needsTermsAcceptance} />;
}

// Task #2812 — Each portal runs its OWN /me check (via its auth provider)
// that resolves a beat after the global one AppContent gates on. These inner
// shells live inside the portal provider so they can read that portal's
// isLoading and show the same on-brand compass loader during the gap, instead
// of briefly painting the portal shell before its identity is known.
function VendorPortalShell() {
  const { isLoading } = useVendorAuth();
  if (isLoading) return <CompassLoader variant="fullscreen" />;
  return (
    <>
      <VendorTermsGate />
      <VendorPasskeyPrompt />
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex flex-col">
          <ErrorBoundary>
            <Router />
          </ErrorBoundary>
        </div>
        <SiteFooter />
      </div>
    </>
  );
}

function AgentPortalShell({ showBannerSpacing }: { showBannerSpacing: boolean }) {
  const { isLoading } = useAgentAuth();
  if (isLoading) return <CompassLoader variant="fullscreen" />;
  return (
    <>
      <AgentTermsGate />
      <AgentPasskeyPrompt />
      <ImpersonationBanner />
      <div className="min-h-screen flex flex-col" style={showBannerSpacing ? { paddingTop: "40px" } : undefined}>
        <div className="flex-1 flex flex-col">
          <ErrorBoundary portal="agent">
            <Router />
          </ErrorBoundary>
        </div>
        <SiteFooter />
      </div>
    </>
  );
}

function PrincipalPortalShell() {
  const { isLoading } = usePrincipalAuth();
  if (isLoading) return <CompassLoader variant="fullscreen" />;
  return (
    <ErrorBoundary>
      <Router />
    </ErrorBoundary>
  );
}

export function AppContent() {
  useRemoveBootFallback();
  const [location] = useLocation();
  const { isImpersonating, isImpersonatingAgent, previewingProjectId, user, isLoading: isAuthLoading } = useAuth();
  const isAdmin = location.startsWith("/admin");
  const isDealRoom = location.startsWith("/deal-room");
  const isDashboard = location.startsWith("/dashboard");
  const isAgent = location === "/agent" || location.startsWith("/agent/");
  const isVendor = location === "/vendor" || location.startsWith("/vendor/");
  const isSponsor = location === "/sponsor" || location.startsWith("/sponsor/");
  const isPrincipal = location === "/principals/dashboard" || location.startsWith("/principals/");
  const forceDark = isDealRoom;
  const hasPlayed = typeof window !== "undefined" && localStorage.getItem("compass-intro-played") === "true";
  const [showIntro, setShowIntro] = useState(!hasPlayed);

  useEffect(() => {
    if (forceDark) {
      const root = document.documentElement;
      const wasDark = root.classList.contains("dark");
      root.classList.add("dark");
      return () => {
        if (!wasDark) {
          const stored = localStorage.getItem("theme");
          if (stored === "light") root.classList.remove("dark");
        }
      };
    }
  }, [forceDark]);

  const handleIntroComplete = useCallback(() => {
    try { localStorage.setItem("compass-intro-played", "true"); } catch {}
    setShowIntro(false);
  }, []);

  const showPasswordChange = user && user.mustChangePassword;
  // Task #1421 — Show the terms gate for investor/sponsor/dashboard users.
  // Admins are exempt. The flag is surfaced by useAuth() which reads
  // needsTermsAcceptance from /api/auth/me and already excludes admins.
  const { needsTermsAcceptance } = useAuth();
  const showTermsGate = needsTermsAcceptance;

  // First-run welcome for investors in the portal shell (/dashboard and
  // /deal-room). It folds Terms & Privacy acceptance and the passkey offer
  // into one short wizard, so while it is still owed the standalone
  // TermsAcceptanceGate and PasskeySetupPrompt stay out of the way. The
  // dialog itself yields on the eligibility questionnaire so it does not
  // cover that form. Admins, sponsors, impersonation and project preview
  // never see it.
  const isPortalInvestor =
    !!user && user.role === "user" && !isImpersonating && !previewingProjectId;
  const investorWelcome = useInvestorWelcome(isPortalInvestor ? String(user.id) : null);
  const { pending: investorWelcomePending, showDialog: showInvestorWelcome } =
    investorWelcomeShellState({
      isPortalInvestor,
      inPortalShell: isDashboard || isDealRoom,
      location,
      welcomeOpen: investorWelcome.open,
      mustChangePassword: !!showPasswordChange,
    });

  // Either flavour of impersonation or preview needs 40px top padding
  // so the fixed banner doesn't cover the page chrome.
  const showBannerSpacing = isImpersonating || isImpersonatingAgent || !!previewingProjectId;

  // Task #2036 — Audience for the floating "Chat on WhatsApp" button.
  // Partner/referral recruitment surfaces get partner-flavoured copy;
  // everything else in the public shell leans into the EB-5 visa angle.
  const whatsAppAudience: WhatsAppAudience =
    location.startsWith("/refer/") ||
    location.startsWith("/agents/apply") ||
    location.startsWith("/agents/lead-referral") ||
    location.startsWith("/agents/application") ||
    location === "/agent-landing" ||
    location.startsWith("/sponsors/apply") ||
    location.startsWith("/sponsors/application")
      ? "partner"
      : "visa-prospect";

  // Detect the public project surfaces so the floating WhatsApp bubble
  // can be lifted above the sticky bottom CTA bar those pages render —
  // the detail page's CTA bar and the index's mobile sign-up bar. On all
  // other public pages the bubble keeps its default bottom-4 position.
  const [isProjectDetail] = useRoute("/projects/:slug");
  const [isProjectsIndex] = useRoute("/projects");

  // While the initial /api/auth/me check is still resolving, show the on-brand
  // compass loader instead of rendering the shell. This hands off seamlessly
  // from the identical compass in the static #boot-fallback (and, on login,
  // bridges the hard navigation into the destination) so there is never a white
  // flash or a maintenance glimpse — and it guarantees the terms-acceptance
  // decision is known before any page paints, so the gate eases in with the
  // page rather than popping in a beat later.
  if (isAuthLoading) {
    return <CompassLoader variant="fullscreen" />;
  }

  if (isVendor) {
    return (
      <VendorAuthProvider>
        <VendorPortalShell />
      </VendorAuthProvider>
    );
  }

  if (isAgent) {
    return (
      <AgentAuthProvider>
        <AgentPortalShell showBannerSpacing={showBannerSpacing} />
      </AgentAuthProvider>
    );
  }

  if (isSponsor) {
    return (
      <>
        {showTermsGate && <TermsAcceptanceGate queryKey="/api/auth/me" />}
        <PasskeySetupPrompt blocked={showTermsGate} />
        <ImpersonationBanner />
        <div className="min-h-screen flex flex-col" style={showBannerSpacing ? { paddingTop: "40px" } : undefined}>
          <div className="flex-1 flex flex-col">
            <ErrorBoundary>
              <Router />
            </ErrorBoundary>
          </div>
          <SiteFooter />
        </div>
      </>
    );
  }

  if (isPrincipal) {
    return (
      <PrincipalAuthProvider>
        <PrincipalPortalShell />
      </PrincipalAuthProvider>
    );
  }

  if (isAdmin || isDealRoom || isDashboard) {
    return (
      <>
        {showPasswordChange && <ForcePasswordChange />}
        {showInvestorWelcome && (
          <InvestorWelcomeModal
            open
            firstName={user?.firstName || user?.name?.split(" ")[0] || null}
            needsTermsAcceptance={needsTermsAcceptance}
            onComplete={investorWelcome.complete}
          />
        )}
        {showTermsGate && !showPasswordChange && !investorWelcomePending && (
          <TermsAcceptanceGate queryKey="/api/auth/me" />
        )}
        {!investorWelcomePending && (
          <PasskeySetupPrompt blocked={!!(showPasswordChange || showTermsGate)} />
        )}
        <ImpersonationBanner />
        <PreviewBanner />
        <div className="min-h-screen flex flex-col" style={showBannerSpacing ? { paddingTop: "40px" } : undefined}>
          <div className="flex-1 flex flex-col">
            <ErrorBoundary portal={isAdmin ? "admin" : "investor"}>
              <Router />
            </ErrorBoundary>
          </div>
          {!isAdmin && <SiteFooter />}
        </div>
      </>
    );
  }

  return (
    <>
      {/* Task #1421 — gate applies on all authenticated routes including
          the public shell (e.g. /projects, /news) so investor/sponsor
          users cannot bypass acceptance by staying on public pages. */}
      {showTermsGate && <TermsAcceptanceGate queryKey="/api/auth/me" />}
      <PasskeySetupPrompt
        blocked={showTermsGate || (isProjectDetail && !!user && !user.accreditationAcknowledgedAt)}
      />
      {showIntro && <CompassIntro onComplete={handleIntroComplete} />}
      <ImpersonationBanner />
      <PreviewBanner />
      <div className="min-h-screen flex flex-col" style={showBannerSpacing ? { paddingTop: "40px" } : undefined}>
        <Header />
        <main className="flex-1">
          <ErrorBoundary portal="public">
            <Router />
          </ErrorBoundary>
        </main>
        {/* Render the minimal footer on all public pages except the home page,
            which already has a comprehensive footer with the cookie link. */}
        {location !== "/" && <SiteFooter />}
      </div>
      {/* Task #2036 — Floating WhatsApp entry point on the public shell
          only. Authenticated portals (admin, dashboard, agent, vendor,
          sponsor, deal-room) return earlier and intentionally skip it
          because they already have an in-app inbox.
          aboveStickyBar lifts the bubble on project detail pages so it
          doesn't overlap the sticky bottom CTA bar. */}
      <WhatsAppFloatingButton
        audience={whatsAppAudience}
        aboveStickyBar={isProjectDetail || isProjectsIndex}
        compact={isProjectsIndex || isProjectDetail}
      />
    </>
  );
}

// Remove the static "We'll be right back" fallback baked into index.html once a
// real UI is confirmed on screen. useLayoutEffect runs after React commits and
// before the browser paints, so the fallback disappears in the same frame the
// app (or the branded crash screen) becomes visible — no flicker over real
// content. If the bundle fails to boot this never runs and the message stays.
function useRemoveBootFallback() {
  useLayoutEffect(() => {
    document.getElementById("boot-fallback")?.remove();
    // Crawler-first body injected by server/lib/ssr-body.ts — remove once
    // React owns the page so users do not see duplicate unstyled copy.
    document.getElementById("ssr-crawler-content")?.remove();
    document.getElementById("ssr-crawler-header")?.remove();
    document.getElementById("ssr-crawler-footer")?.remove();
  }, []);
}

export function SentryFallback({ error }: { error: unknown; resetError?: () => void }) {
  useRemoveBootFallback();

  // If this outermost boundary caught a failed dynamic import of an outdated
  // chunk (the app was deployed under a long-lived tab), recover with a
  // one-time, loop-guarded full reload onto the new version instead of showing
  // the maintenance screen. Only when the reload is suppressed by the loop
  // guard do we fall through to the maintenance copy below. Task #3081.
  const staleChunk = isStaleChunkError(error);
  const [reloadFailed, setReloadFailed] = useState(false);
  useEffect(() => {
    if (staleChunk) {
      if (!attemptStaleChunkReload()) setReloadFailed(true);
    }
  }, [staleChunk]);

  if (staleChunk && !reloadFailed) {
    return <CompassLoader variant="fullscreen" />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-dark-bg px-4">
      <div className="max-w-md w-full text-center">
        <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <svg className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h1 className="font-heading text-2xl font-bold text-white mb-2">We're doing some maintenance</h1>
        <p className="text-[#888] mb-6">We're making a few improvements to the site and will be back shortly. Please check back later &mdash; your information is safe.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2.5 bg-primary text-brand-dark-bg font-semibold rounded-lg hover:bg-primary/90 transition-colors"
          data-testid="button-error-retry"
        >
          Try Again
        </button>
        {import.meta.env.DEV && (
          <details className="mt-6 text-left">
            <summary className="text-xs text-[#666] cursor-pointer hover:text-[#888]">Error details</summary>
            <pre className="mt-2 text-xs text-[#666] bg-brand-card-bg p-3 rounded-lg overflow-auto max-h-32 border border-brand-navy">{error instanceof Error ? error.message : String(error)}</pre>
          </details>
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => <SentryFallback error={error} resetError={resetError} />}
      beforeCapture={(scope, error) => {
        // Deploy artifact ("this tab is outdated"), not a render crash.
        // Tag for client beforeSend + ops-digest filters; successful recoveries
        // are dropped before they open a Sentry issue / Priority Fix.
        if (isStaleChunkError(error)) {
          scope.setTag("kind", "stale_chunk_reload");
          scope.setLevel("info");
        }
      }}
    >
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <AppContent />
            <PortalTransitionOverlay />
            <CookieConsentManager />
            <Toaster />
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default Sentry.withProfiler(App);
